import { useAtomValue } from "jotai";
import { useTradingBalance } from "#/hooks/use-trading-balance";
import { useWallet } from "#/hooks/use-wallet";
import {
	pendingDepositInTransit,
	pendingDepositJustCompleted,
	pendingDepositsAtom,
} from "../stores/pending-deposit";

/**
 * True while a bridge deposit is in-flight ("crediting") for the connected
 * address — the same derivation the deposit dialog uses. Self-terminating via
 * the balance rising or the 15-min TTL, so readers need no cleanup effect.
 * `Date.now()` is evaluated at render like in the dialog; the WS-fresh trading
 * balance and the 30s wallet-balance poll supply re-renders, so don't add a
 * timer — the record lifecycle (and its one timer) lives in deposit-dialog.
 */
export function useDepositInTransit(): boolean {
	const { address } = useWallet();
	const pendingMap = useAtomValue(pendingDepositsAtom);
	const tradingBalance = useTradingBalance();
	const record = address ? pendingMap[address.toLowerCase()] : undefined;
	return pendingDepositInTransit(record, tradingBalance, Date.now());
}

/**
 * True while the post-arrival "completed" check should show (a short window
 * after the deposit lands). Timer-free like the hook above: deposit-dialog's
 * lifecycle effect deletes the record when the window ends, and that atom
 * write re-renders every reader.
 */
export function useDepositJustCompleted(): boolean {
	const { address } = useWallet();
	const pendingMap = useAtomValue(pendingDepositsAtom);
	const record = address ? pendingMap[address.toLowerCase()] : undefined;
	return pendingDepositJustCompleted(record, Date.now());
}
